import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PLANS } from "../lib/plans";
import { trackEvent } from "../lib/analytics";
import Navbar from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Check } from "lucide-react";
import { toast } from "sonner";

export function PricingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);

  const PRICING_PLANS = [
    { key: "fun", name: "Fun Pack", price: PLANS.fun.price, color: "bg-yellow-300", features: [`${PLANS.fun.booksPerMonth} book / month`, "Up to 20 pages", "All art styles", "PDF export"], featured: false },
    { key: "excitement", name: "Excitement", price: PLANS.excitement.price, color: "bg-pink-400", features: [`${PLANS.excitement.booksPerMonth} books / month`, "Up to 20 pages", "Priority generation", "PDF + share"], featured: true },
    { key: "epic", name: "Epic Pack", price: PLANS.epic.price, color: "bg-purple-400", features: [`${PLANS.epic.booksPerMonth} books / month`, "Up to 20 pages", "Priority generation", "Early access"], featured: false },
  ];

  const subscribe = async (plan) => {
    if (!user) { navigate("/signup"); return; }
    setBusy(plan);
    trackEvent("begin_checkout", { plan });
    try {
      const r = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen halftone-purple">
      <Navbar/>
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h1 className="font-headings uppercase text-5xl text-center mb-3" data-testid="pricing-page-title">Pick your power level</h1>
        <p className="text-center font-body font-bold mb-10">Cancel anytime · No ads ever</p>
        <div className="grid md:grid-cols-3 gap-6">
          {PRICING_PLANS.map(p => (
            <div key={p.key} className={`cv-border rounded-2xl p-8 ${p.color} ${p.featured ? "cv-shadow-lg md:-translate-y-4" : "cv-shadow"} relative`} data-testid={`plan-${p.key}`}>
              {p.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-black text-yellow-300 px-4 py-1 font-headings rounded-full">MOST LOVED</div>
              )}
              <h3 className="font-headings uppercase text-3xl">{p.name}</h3>
              <div className="font-headings text-5xl my-3">${p.price}<span className="text-lg">/mo</span></div>
              <ul className="space-y-2 mb-6 font-body font-bold">
                {p.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2"><Check size={20} className="mt-0.5 shrink-0" strokeWidth={3}/>{f}</li>
                ))}
              </ul>
              <Button onClick={()=>subscribe(p.key)} disabled={busy === p.key} className="w-full bg-black text-white hover:bg-zinc-800 font-headings text-lg uppercase rounded-xl py-5" data-testid={`subscribe-${p.key}`}>
                {busy === p.key ? "Redirecting..." : (user ? "Subscribe" : "Get started")}
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function PaymentSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;
    const poll = async (n) => {
      if (stopped || n >= 8) { if (n >= 8) setStatus("timeout"); return; }
      setAttempts(n+1);
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (
          r.data.subscription_status === "active" ||
          r.data.subscription_status === "trialing" ||
          r.data.payment_status === "paid"
        ) {
          setStatus("paid");
          const updatedUser = await refresh();
          trackEvent("purchase", {
            transaction_id: sessionId,
            value: (r.data.amount_total || 0) / 100,
            currency: (r.data.currency || "cad").toUpperCase(),
            plan: updatedUser?.plan,
          });
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
      } catch (e) {
        console.error("Payment status poll failed:", e);
      }
      setTimeout(() => poll(n+1), 2500);
    };
    poll(0);
    return () => { stopped = true; };
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen halftone-yellow">
      <Navbar/>
      <div className="max-w-md mx-auto px-6 py-16">
        <div className="cv-border cv-shadow-lg rounded-2xl bg-white p-8 text-center" data-testid="payment-status-card">
          {status === "checking" && (<>
            <div className="text-5xl mb-3">⏳</div>
            <h1 className="font-headings uppercase text-3xl mb-2">Confirming...</h1>
            <p className="font-body font-bold">Attempt {attempts}/8</p>
          </>)}
          {status === "paid" && (<>
            <div className="text-5xl mb-3">🎉</div>
            <h1 className="font-headings uppercase text-3xl mb-2">You're in!</h1>
            <p className="font-body font-bold mb-6">Your subscription is active. Time to make some comics!</p>
            <Link to="/dashboard"><Button className="bg-purple-600 hover:bg-purple-500 text-white cv-border cv-shadow cv-press font-headings text-xl uppercase rounded-xl py-5 px-8" data-testid="payment-go-dashboard">Go to dashboard</Button></Link>
          </>)}
          {(status === "timeout" || status === "expired" || status === "error") && (<>
            <div className="text-5xl mb-3">⚠️</div>
            <h1 className="font-headings uppercase text-3xl mb-2">Hmm.</h1>
            <p className="font-body font-bold mb-6">We couldn't confirm your payment. If you were charged, contact support — otherwise, try again.</p>
            <Link to="/pricing"><Button className="bg-yellow-400 cv-border cv-shadow cv-press font-headings uppercase rounded-xl">Back to pricing</Button></Link>
          </>)}
        </div>
      </div>
    </div>
  );
}
